import type { Icon } from "@phosphor-icons/react";
import { ChartLineUpIcon } from "@phosphor-icons/react/dist/csr/ChartLineUp";
import { GaugeIcon } from "@phosphor-icons/react/dist/csr/Gauge";
import { HandshakeIcon } from "@phosphor-icons/react/dist/csr/Handshake";
import { MegaphoneIcon } from "@phosphor-icons/react/dist/csr/Megaphone";
import { ReceiptIcon } from "@phosphor-icons/react/dist/csr/Receipt";
import { ScrollIcon } from "@phosphor-icons/react/dist/csr/Scroll";
import { WalletIcon } from "@phosphor-icons/react/dist/csr/Wallet";
import { PAY_BREAKDOWN_PATH } from "@/payroll/pay-breakdown/routes";
import { PAYROLL_EDITOR_PATH } from "@/payroll/settings/routes";
import { PROFITABILITY_PATH } from "@/profitability/routes";

export interface SidebarSection {
  label: string;
  to: string;
  isActive: boolean;
  icon?: Icon;
  group?: string;
  count?: number;
  countIsPartial?: boolean;
  countLabel?: string;
  warning?: string;
  /** Holds a row's place while the list it belongs to loads. */
  pending?: boolean;
  /** Rows nested under this one, once its data has loaded. */
  children?: SidebarSection[];
  /** Whether the row is expanded. The list that owns the row decides. */
  expanded?: boolean;
  /**
   * The list's answer to the chevron. A tree row sets this: the shell does
   * not keep a second expanded state, and the chevron does not navigate.
   */
  onExpandedChange?: (expanded: boolean) => void;
  /** The reader is reaching for the row (hover, focus): warm its children. */
  onIntent?: () => void;
  /** A button at the end of the row, such as an agent's Settings. */
  action?: SidebarRowAction;
}

export interface SidebarRowAction {
  label: string;
  to: string;
  icon: Icon;
  isActive: boolean;
}

export const NAV_ROUTES = {
  dashboard: "/dashboard",
  referrals: "/referrals",
  campaigns: "/campaigns",
  payBreakdown: PAY_BREAKDOWN_PATH,
  payrollRules: PAYROLL_EDITOR_PATH,
  profitability: PROFITABILITY_PATH,
  accountsPayable: "/accounts-payable",
} as const;

export interface NavVisibility {
  enablePocketflows: boolean;
  hasPayBreakdownAccess: boolean;
  canEditPayrollRules: boolean;
  enableReferrals: boolean;
  enableAp: boolean;
  canSeePayrollPreview: boolean;
  canSeeApPreview: boolean;
}

export type NavGroupId = "referrals" | "operations";

export type NavPanelId = "payroll";

const NAV_PANEL: Record<NavPanelId, { label: string; icon: Icon }> = {
  payroll: { label: "Payroll", icon: WalletIcon },
};

export interface NavItemDef {
  href: string;
  label: string;
  icon: Icon;
  group: NavGroupId;
  panel?: NavPanelId;
  isNew?: boolean;
  show?: (visibility: NavVisibility) => boolean;
}

export const foldNavPanels = <
  T extends NavItemDef & {
    isActive: boolean;
    to: string;
    sections: SidebarSection[];
  },
>(
  items: T[],
): T[] => {
  const groups = new Map<NavPanelId, T[]>();
  const ordered: T[] = [];
  for (const item of items) {
    if (!item.panel) {
      ordered.push(item);
      continue;
    }
    const group = groups.get(item.panel);
    if (group) {
      group.push(item);
      continue;
    }
    groups.set(item.panel, [item]);
    ordered.push(item);
  }
  return ordered.flatMap((item) => {
    if (!item.panel) return [item];
    const group = groups.get(item.panel);
    if (!group || group.length === 1) return [item];
    const panel = NAV_PANEL[item.panel];
    const sections: SidebarSection[] = group.flatMap(
      (member): SidebarSection[] => {
        if (member.sections.length === 0) {
          return [
            {
              label: member.label,
              to: member.to,
              isActive: member.isActive,
              icon: member.icon,
            },
          ];
        }
        const sectionActive = member.sections.some(
          (section) => section.isActive,
        );
        return [
          {
            label: member.label,
            to: member.to,
            isActive: member.isActive && !sectionActive,
            icon: member.icon,
            group: member.label,
          },
          ...member.sections.map((section) => ({
            ...section,
            group: section.group ?? member.label,
          })),
        ];
      },
    );
    return [
      {
        ...group[0],
        label: panel.label,
        icon: panel.icon,
        isActive: group.some((member) => member.isActive),
        to: group[0].to,
        sections,
      },
    ];
  });
};

export const NAV_ITEM_DEFS: NavItemDef[] = [
  {
    href: NAV_ROUTES.dashboard,
    label: "Dashboard",
    icon: GaugeIcon,
    group: "referrals",
    show: ({ enableReferrals }) => enableReferrals,
  },
  {
    href: NAV_ROUTES.referrals,
    label: "Referrals",
    icon: HandshakeIcon,
    group: "referrals",
    show: ({ enableReferrals }) => enableReferrals,
  },
  {
    href: NAV_ROUTES.campaigns,
    label: "Campaigns",
    icon: MegaphoneIcon,
    group: "referrals",
    show: ({ enablePocketflows, enableReferrals }) =>
      enableReferrals && enablePocketflows,
  },
  {
    href: NAV_ROUTES.profitability,
    label: "Job Costing",
    icon: ChartLineUpIcon,
    group: "operations",
    isNew: true,
  },
  {
    href: NAV_ROUTES.accountsPayable,
    label: "Accounts payable",
    icon: ReceiptIcon,
    group: "operations",
    show: ({ enableAp, canSeeApPreview }) => enableAp || canSeeApPreview,
  },
  {
    href: NAV_ROUTES.payBreakdown,
    label: "Payroll",
    icon: WalletIcon,
    group: "operations",
    panel: "payroll",
    show: ({ hasPayBreakdownAccess, canSeePayrollPreview }) =>
      hasPayBreakdownAccess || canSeePayrollPreview,
  },
  {
    href: NAV_ROUTES.payrollRules,
    label: "Pay rules",
    icon: ScrollIcon,
    group: "operations",
    panel: "payroll",
    show: ({ canEditPayrollRules }) => canEditPayrollRules,
  },
];

export const isPathUnder = (pathname: string, href: string) => {
  const after = pathname.slice(href.length);
  return pathname.startsWith(href) && (after === "" || after.startsWith("/"));
};
